import React, { ReactElement } from 'react';
import { CustomError } from 'form/yup/reactHookFormResolver';
import ErrorMessage, { CustomErrorMessage } from 'form/components/CustomErrorMessage/CustomErrorMessage';
import {
  InputContainer,
  InputLabel,
  ChildrenContainer,
  ExtraContent,
  ExtraContentText,
  HelpText,
  InputError,
} from './InputWrapper.styled';
import { Context, customErrorMessageClassName } from 'form/components/CustomValidation/CustomValidation';

export interface InjectableContent {
  position: 'top' | 'bottom';
  variant: 'text' | 'error' | 'warning' | 'success' | 'component';
  content: string | JSX.Element;
}

export interface Help {
  text: string;
  onClick: () => void;
}

type Props = {
  name: string;
  title: string;
  children: ReactElement;
  error?: CustomError[];
  customErrorMessages?: CustomErrorMessage[];
  isOptional?: boolean;
  extraContent?: InjectableContent[];
  showMultipleErrors?: boolean;
  help?: Help;
  customValidation?: { context: Context; shouldShowError: (name: string) => boolean };
  style?: React.CSSProperties;
  isNested?: boolean;
  newUi?: boolean;
};

const filterByPosition = (position: 'top' | 'bottom') => {
  return (item: NonNullable<Props['extraContent']>[0]) => {
    return item.position === position;
  };
};

const ExtraContentArea = ({ contents, name }: { contents: NonNullable<Props['extraContent']>; name: string }) => {
  return contents.length > 0 ? (
    <ExtraContent>
      {contents.map((content, i) => (
        <React.Fragment key={`content-${name}-${i}`}>
          {content.variant === 'component' ? (
            content.content
          ) : (
            <ExtraContentText variant={content.variant}>{content.content}</ExtraContentText>
          )}
        </React.Fragment>
      ))}
    </ExtraContent>
  ) : (
    <></>
  );
};

const InputWrapper: React.FC<Props> = ({
  name,
  title,
  children,
  error,
  customErrorMessages,
  isOptional,
  extraContent = [],
  showMultipleErrors = false,
  help,
  customValidation,
  style,
  isNested,
  newUi = false,
}) => {
  const [upperContents, bottomContents] = [
    extraContent.filter(filterByPosition('top')),
    extraContent.filter(filterByPosition('bottom')),
  ];

  return (
    <InputContainer style={style} isNested={isNested}>
      {title && (
        <InputLabel htmlFor={name} error={error && newUi} newUi={newUi}>
          {title}
          {help && <HelpText onClick={help.onClick}>{help.text}</HelpText>}
          {isOptional && <span>{` (optional)`}</span>}
        </InputLabel>
      )}
      <ExtraContentArea contents={upperContents} name={`upper-${name}`} />
      <ChildrenContainer>{children}</ChildrenContainer>
      {error && (
        <ErrorMessage error={error} customErrorMessages={customErrorMessages} showMultipleErrors={showMultipleErrors} />
      )}
      {customValidation && customValidation.shouldShowError(name) && (
        <div className={customErrorMessageClassName} data-field-name={name}>
          <InputError>{customValidation.context.errors[name]}</InputError>
        </div>
      )}
      <ExtraContentArea contents={bottomContents} name={`bottom-${name}`} />
    </InputContainer>
  );
};

export default InputWrapper;
